import type { InvoicesQueryParams, InvoicesResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils/logging';

import {
  zodInvoicesQueryParamsSchema,
  zodInvoicesResponseSchema,
} from './validations';

/**
 * Fetches all invoices from the API with the given query parameters.
 *
 * @param queryParams - The query parameters for fetching invoices.
 * @returns The paginated invoice data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoices_for_user_invoice_get
 */
export const fetchInvoices = async (
  queryParams: Partial<InvoicesQueryParams>,
  options: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.INVOICE.GET_ALL(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodInvoicesQueryParamsSchema.transform((data) =>
          withoutObjectKeysWhereValueIs(data, [undefined]),
        ),
      },
      ...options,
    })
    .json<InvoicesResponse | undefined>();

  // Validate the returned data and log any validation errors
  const validatedResponse = zodInvoicesResponseSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Invoices API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
